import React, { useState, useEffect, useRef } from "react";
import { Link } from 'react-router-dom';
import "./Contact.css";
import "./Modal.css";
import "./ErrorMessages.css";
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Contact = ({ isOpen, onClose, theme }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState(null); // null, 'submitting', 'success', 'error'
  const modalRef = useRef(null);
  const MESSAGE_MAX_LENGTH = 80;

  // --- Focus trap ---
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleResetAndClose();
        return;
      }
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Focus the modal when it opens
    const timer = setTimeout(() => {
      if (modalRef.current) {
        const firstInput = modalRef.current.querySelector('input, button');
        if (firstInput) firstInput.focus();
      }
    }, 100);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePhoneChange = (e) => {
    const input = e.target.value.replace(/\D/g, "").substring(0, 10);
    let formattedPhone = "";
    if (input.length > 0) {
      formattedPhone = input.substring(0, 3);
    }
    if (input.length > 3) {
      formattedPhone += "-" + input.substring(3, 6);
    }
    if (input.length > 6) {
      formattedPhone += "-" + input.substring(6, 10);
    }
    setPhone(formattedPhone);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = "Name is required";
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      newErrors.email = "Invalid email format";
    }
    const rawPhone = phone.replace(/\D/g, "");
    if (rawPhone.length !== 10) {
      newErrors.phone = "Please enter a valid 10-digit phone number.";
    }
    if (!message.trim()) {
      newErrors.message = "Message is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetAndClose = () => {
    onClose();
    setTimeout(() => {
        setName("");
        setEmail("");
        setPhone("");
        setMessage("");
        setErrors({});
        setSubmissionStatus(null);
    }, 500);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setSubmissionStatus("submitting");

    const sanitizedData = {
      name: DOMPurify.sanitize(name),
      email: DOMPurify.sanitize(email),
      phone: DOMPurify.sanitize(phone),
      message: DOMPurify.sanitize(message)
    }

    try {
      const response = await fetch(`${API_URL}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: sanitizedData.name,
          email: sanitizedData.email,
          phone: sanitizedData.phone,
          message: sanitizedData.message,
        }),
      });

      if (response.ok) {
        setSubmissionStatus("success");
        setTimeout(handleResetAndClose, 3000);
      } else {
        setSubmissionStatus("error");
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmissionStatus("error");
    }
  };

  const renderContent = () => {
    switch (submissionStatus) {
      case "success":
        return (
          <div className="submission-feedback" aria-live="polite">
            <div className="tick-mark">✓</div>
            <h3>Message Sent!</h3>
            <p>Thank you for reaching out. We will get back to you shortly.</p>
          </div>
        );
      case "error":
        return (
          <div className="submission-feedback" aria-live="polite">
            <div className="error-mark">✗</div>
            <h3>Submission Failed</h3>
            <p>Something went wrong. Please check your connection and try again.</p>
            <button onClick={() => setSubmissionStatus(null)} className="try-again-button">
              Try Again
            </button>
          </div>
        );
      default:
        return (
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="contact-name">Your Name</label>
              <input id="contact-name" type="text" name="name" value={name} onChange={(e) => setName(e.target.value)} maxLength="50" required />
              {errors.name && <p className="error-message">{errors.name}</p>}
            </div>
            <div className="form-group">
              <label htmlFor="contact-email">Your Email</label>
              <input id="contact-email" type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              {errors.email && <p className="error-message">{errors.email}</p>}
            </div>
            <div className="form-group">
              <label htmlFor="contact-phone">Phone Number</label>
              <input id="contact-phone" type="tel" name="phone" placeholder="e.g., 781-888-1234" value={phone} onChange={handlePhoneChange} required />
              {errors.phone && <p className="error-message">{errors.phone}</p>}
            </div>
            <div className="form-group">
              <label htmlFor="contact-message">Your Message</label>
              <textarea id="contact-message" name="message" rows="4" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={MESSAGE_MAX_LENGTH} required></textarea>
              <div className="char-counter">{message.length}/{MESSAGE_MAX_LENGTH}</div>
              {errors.message && <p className="error-message">{errors.message}</p>}
            </div>
            <button type="submit" disabled={submissionStatus === "submitting"}>
              {submissionStatus === "submitting" ? "Sending..." : "Send Message"}
            </button>
            <p style={{ fontSize: '0.8rem', fontStyle: 'italic', marginTop: '16px', textAlign: 'center', color: 'var(--text-muted)' }}>
                By submitting this form, you acknowledge and agree to our <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-muted)' }}>Terms & Conditions</Link>.
            </p>
          </form>
        );
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleResetAndClose()} role="dialog" aria-modal="true" aria-labelledby="contact-modal-title">
      <div className="modal-content" ref={modalRef}>
        <button className="modal-close-button" onClick={handleResetAndClose} aria-label="Close dialog">×</button>
        <h2 id="contact-modal-title">Contact Us</h2>
        {renderContent()}
      </div>
    </div>
  );
};

export default Contact;
